import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from 'service/axiosInstance';
import {
  AddCommentPayload,
  RepliesPageResponse,
  WorkItemDetailsResponse,
  WorkItemHistory,
  AddTimeEntryPayload,
  UpdateTimeEntryPayload,
} from 'types/task';
import { QUERY_KEYS } from 'service/querykeys';
import { showToast } from 'utils/toast';

// ---------------------------------------------------------------------------
// Comment APIs
// ---------------------------------------------------------------------------

export const addComment = async (payload: AddCommentPayload) => {
  const response = await axiosInstance.post('pmo/add-comment-to-work-item', payload);
  return response.data;
};

export const getReplies = async (
  projectUuid: string,
  workItemUuid: string,
  commentUuid: string,
  page: number = 0,
  size: number = 20,
): Promise<RepliesPageResponse> => {
  const response = await axiosInstance.get(
    `pmo/project/${projectUuid}/workItem/${workItemUuid}/comment/${commentUuid}/replies`,
    { params: { page, size } },
  );
  return response.data.response ?? response.data;
};

export const useGetRepliesQuery = (
  projectUuid: string,
  workItemUuid: string,
  commentUuid: string,
  page: number = 0,
  enabled: boolean = true,
) =>
  useQuery<RepliesPageResponse>({
    queryKey: [...QUERY_KEYS.COMMENT_REPLIES(projectUuid, workItemUuid, commentUuid), page],
    queryFn: () => getReplies(projectUuid, workItemUuid, commentUuid, page),
    enabled,
  });

export const useAddCommentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addComment,

    onMutate: async (newComment) => {
      // For replies: skip optimistic update — invalidate parent replies cache on settle
      if (newComment.parentCommentUuid) {
        return {
          isReply: true,
          parentCommentUuid: newComment.parentCommentUuid,
          projectUuid: newComment.projectUuid,
          workItemUuid: newComment.workItemUuid,
        };
      }

      // For root comments: optimistic insert into task cache
      const queryKey = QUERY_KEYS.TASK(newComment.projectUuid, newComment.workItemUuid);
      await queryClient.cancelQueries({ queryKey });
      const previousTaskData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (oldTask: any) => {
        if (!oldTask) return oldTask;
        const fakeComment = {
          commentUuid: newComment.commentUuid,
          commentDescription: newComment.commentDescription,
          commentedByAppUserFullName: 'You',
          commentedAt: new Date().toISOString(),
          depth: 0,
          isReply: false,
          replyCount: 0,
          hasReplies: false,
          isDeletedPlaceholder: false,
        };
        return {
          ...oldTask,
          workItemCommentDTOList: [fakeComment, ...(oldTask.workItemCommentDTOList || [])],
        };
      });

      return { isReply: false, previousTaskData, queryKey };
    },

    onError: (err, _vars, context: any) => {
      if (!context?.isReply && context?.previousTaskData) {
        queryClient.setQueryData(context.queryKey, context.previousTaskData);
      }
    },

    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.isReply) {
        // Reload parent's replies + update root task replyCount
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.COMMENT_REPLIES(
            context.projectUuid,
            context.workItemUuid,
            context.parentCommentUuid,
          ),
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.TASK(context.projectUuid, context.workItemUuid),
        });
      } else if (context?.queryKey) {
        queryClient.invalidateQueries({ queryKey: context.queryKey });
      }

      // Bust the parent's children-infinite cache so counts update without refresh
      const projectUuid = context?.projectUuid ?? _vars?.projectUuid;
      const workItemUuid = context?.workItemUuid ?? _vars?.workItemUuid;
        const queriesData = queryClient.getQueriesData<any>({
          queryKey: ['task', projectUuid, workItemUuid],
        });
        let parentUuid: string | undefined;
        for (const [, data] of queriesData) {
          if (data) {
            parentUuid = data.workItemParentWorkItemUuid || data.parentWorkItemUuid;
            if (parentUuid) break;
          }
        }
        if (parentUuid) {
          queryClient.invalidateQueries({
            queryKey: ['task-children-infinite', projectUuid, parentUuid],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: ['task', projectUuid, parentUuid],
            exact: false,
          });
        }
    },
  });
};

// ---------------------------------------------------------------------------
// Delete Comment
// ---------------------------------------------------------------------------

export const deleteComment = async ({
  projectUuid,
  workItemUuid,
  commentUuid,
}: {
  projectUuid: string;
  workItemUuid: string;
  commentUuid: string;
}) => {
  const response = await axiosInstance.delete(
    `pmo/project/${projectUuid}/workItem/${workItemUuid}/delete-comment/${commentUuid}`,
  );
  return response.data;
};

type DeleteCommentVariables = {
  projectUuid: string;
  workItemUuid: string;
  commentUuid: string;
  /** UI-only: used for reply cache invalidation, not sent to API */
  parentCommentUuid?: string;
};

export const useDeleteCommentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectUuid, workItemUuid, commentUuid }: DeleteCommentVariables) =>
      deleteComment({ projectUuid, workItemUuid, commentUuid }),

    onMutate: async ({ projectUuid, workItemUuid, commentUuid, parentCommentUuid }) => {
      const taskQueryKey = QUERY_KEYS.TASK(projectUuid, workItemUuid);
      await queryClient.cancelQueries({ queryKey: taskQueryKey });
      const previousTaskData = queryClient.getQueryData(taskQueryKey);

      if (parentCommentUuid) {
        // Optimistically remove from parent's reply cache (all loaded pages)
        const replyBase = QUERY_KEYS.COMMENT_REPLIES(projectUuid, workItemUuid, parentCommentUuid);
        queryClient.setQueriesData({ queryKey: replyBase, exact: false }, (old: any) => {
          if (!old) return old;
          return { ...old, data: (old.data || []).filter((c: any) => c.commentUuid !== commentUuid) };
        });
      } else {
        // Optimistically remove from root task cache
        queryClient.setQueryData(taskQueryKey, (oldTask: any) => {
          if (!oldTask || !oldTask.workItemCommentDTOList) return oldTask;
          return {
            ...oldTask,
            workItemCommentDTOList: oldTask.workItemCommentDTOList.filter(
              (c: any) => c.commentUuid !== commentUuid,
            ),
          };
        });
      }

      return { previousTaskData, taskQueryKey, parentCommentUuid, projectUuid, workItemUuid };
    },

    onError: (_err, _vars, context: any) => {
      if (context?.previousTaskData) {
        queryClient.setQueryData(context.taskQueryKey, context.previousTaskData);
      }
    },

    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.taskQueryKey) {
        queryClient.invalidateQueries({ queryKey: context.taskQueryKey });
      }
      if (context?.parentCommentUuid) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.COMMENT_REPLIES(
            context.projectUuid,
            context.workItemUuid,
            context.parentCommentUuid,
          ),
          exact: false,
        });
      }

      if (context?.projectUuid && context?.workItemUuid) {
        const queriesData = queryClient.getQueriesData<any>({
          queryKey: ['task', context.projectUuid, context.workItemUuid],
        });
        let parentUuid: string | undefined;
        for (const [, data] of queriesData) {
          if (data) {
            parentUuid = data.workItemParentWorkItemUuid || data.parentWorkItemUuid;
            if (parentUuid) break;
          }
        }
        if (parentUuid) {
          queryClient.invalidateQueries({
            queryKey: ['task-children-infinite', context.projectUuid, parentUuid],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: ['task', context.projectUuid, parentUuid],
            exact: false,
          });
        }
      }
    },
  });
};

// ---------------------------------------------------------------------------
// Update Comment
// ---------------------------------------------------------------------------

export const updateComment = async ({
  projectUuid,
  workItemUuid,
  commentUuid,
  commentDescription,
  mentionedAppUsersUuidList,
}: {
  projectUuid: string;
  workItemUuid: string;
  commentUuid: string;
  commentDescription: string;
  mentionedAppUsersUuidList?: string[];
}) => {
  const response = await axiosInstance.post('pmo/update-comment-to-work-item', {
    commentUuid,
    projectUuid,
    workItemUuid,
    commentDescription,
    mentionedAppUsersUuidList,
  });
  return response.data;
};

type UpdateCommentVariables = {
  projectUuid: string;
  workItemUuid: string;
  commentUuid: string;
  commentDescription: string;
  mentionedAppUsersUuidList?: string[];
  /** UI-only: used for reply cache invalidation, not sent to API */
  parentCommentUuid?: string;
};

export const useUpdateCommentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectUuid,
      workItemUuid,
      commentUuid,
      commentDescription,
      mentionedAppUsersUuidList,
    }: UpdateCommentVariables) =>
      updateComment({ projectUuid, workItemUuid, commentUuid, commentDescription, mentionedAppUsersUuidList }),

    onMutate: async ({ projectUuid, workItemUuid, commentUuid, commentDescription, parentCommentUuid }) => {
      const taskQueryKey = QUERY_KEYS.TASK(projectUuid, workItemUuid);
      await queryClient.cancelQueries({ queryKey: taskQueryKey });
      const previousTaskData = queryClient.getQueryData(taskQueryKey);

      if (parentCommentUuid) {
        const replyBase = QUERY_KEYS.COMMENT_REPLIES(projectUuid, workItemUuid, parentCommentUuid);
        queryClient.setQueriesData({ queryKey: replyBase, exact: false }, (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: (old.data || []).map((c: any) =>
              c.commentUuid === commentUuid ? { ...c, commentDescription, isEdited: true } : c,
            ),
          };
        });
      } else {
        queryClient.setQueryData(taskQueryKey, (oldTask: any) => {
          if (!oldTask || !oldTask.workItemCommentDTOList) return oldTask;
          return {
            ...oldTask,
            workItemCommentDTOList: oldTask.workItemCommentDTOList.map((c: any) =>
              c.commentUuid === commentUuid ? { ...c, commentDescription, isEdited: true } : c,
            ),
          };
        });
      }

      return { previousTaskData, taskQueryKey, parentCommentUuid, projectUuid, workItemUuid };
    },

    onError: (_err, _vars, context: any) => {
      if (context?.previousTaskData) {
        queryClient.setQueryData(context.taskQueryKey, context.previousTaskData);
      }
    },

    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.taskQueryKey) {
        queryClient.invalidateQueries({ queryKey: context.taskQueryKey });
      }
      if (context?.parentCommentUuid) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.COMMENT_REPLIES(
            context.projectUuid,
            context.workItemUuid,
            context.parentCommentUuid,
          ),
          exact: false,
        });
      }
    },
  });
};

// ---------------------------------------------------------------------------
// Work Item APIs
// ---------------------------------------------------------------------------

export const getWorkItemDetails = async (
  projectUuid: string,
  workItemUuid: string,
  childPage: number = 0,
  childSize: number = 0,
  includeChildren: boolean = false,
): Promise<WorkItemDetailsResponse> => {
  const response = await axiosInstance.get(
    `pmo/project/${projectUuid}/get-work-item/${workItemUuid}`,
    { params: { childPage, childSize, includeChildren } },
  );
  return response.data.response;
};

const formatStartOfDay = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0] + 'T00:00:00Z';
  } catch {
    return '';
  }
};

const formatEndOfDay = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0] + 'T23:59:59Z';
  } catch {
    return '';
  }
};

export const getWorkItemChildren = async (
  projectUuid: string,
  workItemUuid: string,
  childCursor: string | null = null,
  size: number = 20,
  filters?: any
): Promise<any> => {
  // Build URLSearchParams to correctly handle repeated params for array fields
  const params = new URLSearchParams();

  params.append('size', String(size));
  params.append('sortBy', filters?.sortBy || 'listOrder');
  params.append('sortDirection', filters?.sortDirection || 'ASC');

  if (childCursor) params.append('cursor', childCursor);
  if (filters?.hideCompleted) params.append('hideDoneWorkItem', 'true');
  if (filters?.searchTerm) params.append('search', filters.searchTerm);
  if (filters?.assignees) params.append('assigneeUuid', filters.assignees);
  if (filters?.reporters) params.append('reporterUuid', filters.reporters);

  // Date params in ISO datetime format
  if (filters?.createdDateRange?.from) {
    const formatted = formatStartOfDay(filters.createdDateRange.from);
    if (formatted) params.append('createdDateFrom', formatted);
  }
  if (filters?.createdDateRange?.to) {
    const formatted = formatEndOfDay(filters.createdDateRange.to);
    if (formatted) params.append('createdDateTo', formatted);
  }
  if (filters?.dueDateRange?.from) {
    const formatted = formatStartOfDay(filters.dueDateRange.from);
    if (formatted) params.append('dueDateFrom', formatted);
  }
  if (filters?.dueDateRange?.to) {
    const formatted = formatEndOfDay(filters.dueDateRange.to);
    if (formatted) params.append('dueDateTo', formatted);
  }

  // Repeated params for list fields
  if (filters?.statuses?.length > 0) {
    filters.statuses.forEach((id: string) => params.append('workStatusUuidList', id));
  }
  if (filters?.priorities?.length > 0) {
    filters.priorities.forEach((p: string) => params.append('workItemPriorityList', p));
  }
  if (filters?.taskTypes?.length > 0) {
    filters.taskTypes.forEach((id: string) => params.append('workTypeUuidList', id));
  }

  const response = await axiosInstance.get(
    `pmo/project/${projectUuid}/get-work-item-children/${workItemUuid}`,
    { params }
  );
  return response.data;
};

export const getWorkItemHistory = async (
  projectUuid: string,
  workItemUuid: string,
): Promise<WorkItemHistory[]> => {
  const response = await axiosInstance.get(
    `pmo/project/${projectUuid}/get-work-item-history/${workItemUuid}`,
  );
  return response.data.response;
};

export const deleteWorkItem = async ({
  projectUuid,
  workItemUuid,
}: {
  projectUuid: string;
  workItemUuid: string;
}) => {
  const response = await axiosInstance.delete(
    `pmo/project/${projectUuid}/delete-work-item/${workItemUuid}`,
  );
  return response.data;
};

export const useDeleteWorkItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWorkItem,
    onSuccess: (data, variables) => {
      showToast.success('Work item deleted successfully');
      queryClient.resetQueries({ queryKey: ['tasks'], exact: false });
      if (variables.projectUuid) {
        queryClient.resetQueries({ queryKey: ['tasks', variables.projectUuid], exact: false });
        // BROAD INVALIDATION: Refresh all task details in the project to catch parent/link updates
        queryClient.invalidateQueries({ queryKey: ['task', variables.projectUuid] });
        queryClient.invalidateQueries({ queryKey: ['task-children', variables.projectUuid], exact: false });
        queryClient.invalidateQueries({ queryKey: ['task-children-infinite', variables.projectUuid], exact: false });
      }
    },
  });
};

// ---------------------------------------------------------------------------
// Time Entry APIs
// ---------------------------------------------------------------------------

export const addTimeEntry = async (payload: AddTimeEntryPayload) => {
  const response = await axiosInstance.post('pmo/add-time-entry-to-work-item', payload);
  return response.data;
};

export const updateTimeEntry = async (payload: UpdateTimeEntryPayload) => {
  const response = await axiosInstance.post('pmo/update-time-entry-to-work-item', payload);
  return response.data;
};

export const deleteTimeEntry = async ({
  projectUuid,
  workItemUuid,
  timeEntryUuid,
}: {
  projectUuid: string;
  workItemUuid: string;
  timeEntryUuid: string;
}) => {
  const response = await axiosInstance.delete(
    `pmo/project/${projectUuid}/workItem/${workItemUuid}/delete-time-entry/${timeEntryUuid}`,
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Comment Reaction APIs
// ---------------------------------------------------------------------------

export const toggleCommentReaction = async ({
  projectUuid,
  workItemUuid,
  commentUuid,
  emoji,
}: {
  projectUuid: string;
  workItemUuid: string;
  commentUuid: string;
  emoji: string;
}) => {
  const response = await axiosInstance.post(
    `pmo/project/${projectUuid}/workItem/${workItemUuid}/comment/${commentUuid}/toggle-reaction`,
    { emoji },
  );
  return response.data;
};

type ToggleReactionVariables = {
  projectUuid: string;
  workItemUuid: string;
  commentUuid: string;
  emoji: string;
  /** UI-only: set when reacting to a reply */
  parentCommentUuid?: string;
};

export const useToggleCommentReactionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectUuid, workItemUuid, commentUuid, emoji }: ToggleReactionVariables) =>
      toggleCommentReaction({ projectUuid, workItemUuid, commentUuid, emoji }),

    onMutate: async ({ projectUuid, workItemUuid, commentUuid, emoji, parentCommentUuid }) => {
      const queryKey = QUERY_KEYS.TASK(projectUuid, workItemUuid);
      await queryClient.cancelQueries({ queryKey });
      const previousTaskData = queryClient.getQueryData(queryKey);

      const applyReaction = (comments: any[]) =>
        comments.map((comment: any) => {
          if (comment.commentUuid !== commentUuid) return comment;
          const existingReactions = comment.reactions || [];
          const idx = existingReactions.findIndex((r: any) => r.emoji === emoji);
          let newReactions = [...existingReactions];
          if (idx > -1) {
            const r = newReactions[idx];
            if (r.reactedByCurrentUser) {
              if (r.count <= 1) newReactions.splice(idx, 1);
              else newReactions[idx] = { ...r, count: r.count - 1, reactedByCurrentUser: false };
            } else {
              newReactions[idx] = { ...r, count: r.count + 1, reactedByCurrentUser: true };
            }
          } else {
            newReactions.push({ emoji, count: 1, reactedByCurrentUser: true, reactedUsers: [] });
          }
          return { ...comment, reactions: newReactions };
        });

      if (parentCommentUuid) {
        // Optimistically update reply cache
        const replyBase = QUERY_KEYS.COMMENT_REPLIES(projectUuid, workItemUuid, parentCommentUuid);
        queryClient.setQueriesData({ queryKey: replyBase, exact: false }, (old: any) => {
          if (!old) return old;
          return { ...old, data: applyReaction(old.data || []) };
        });
      } else {
        // Optimistically update root task cache
        queryClient.setQueryData(queryKey, (oldTask: any) => {
          if (!oldTask || !oldTask.workItemCommentDTOList) return oldTask;
          return { ...oldTask, workItemCommentDTOList: applyReaction(oldTask.workItemCommentDTOList) };
        });
      }

      return { previousTaskData, queryKey, parentCommentUuid, projectUuid, workItemUuid };
    },

    onError: (_err, _vars, context: any) => {
      if (context?.previousTaskData) {
        queryClient.setQueryData(context.queryKey, context.previousTaskData);
      }
    },

    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.queryKey) queryClient.invalidateQueries({ queryKey: context.queryKey });
      if (context?.parentCommentUuid) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.COMMENT_REPLIES(
            context.projectUuid,
            context.workItemUuid,
            context.parentCommentUuid,
          ),
          exact: false,
        });
      }
    },
  });
};
